import ReactDOM from 'react-dom/client';
import html2canvas from 'html2canvas';
import clsx from 'clsx';
import { useMemo, useRef } from 'react';
import ShippingInstructionDescription from './shipping-instruction';
import {
  ShipmentDetails,
  ShippingPickupTypeEnum,
  TimelineShippingLabelAction,
} from '@/external/timeline/interfaces/timeline';
import { Product } from '@/external/timeline/interfaces/orderSaleProduct';
import { onImageError, productImage } from '@/external/timeline/helper';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/external/timeline/fake';
import { SellerShippingAddress } from '@/types/item';
import styles from './shipping.module.scss';
import AddressCard from './address-card';
import UrlActionView from '../url-action';
import PopupActionView from '../popup-action';

const langPrefix = 'MY_ACCOUNT.ORDERS_SALES.ACTION.GET_SHIPPING_LABEL.';
const classNamePath = 'shippingInfo';

interface ShippingInfoProps {
  shipmentDetails?: ShipmentDetails;
  action?: TimelineShippingLabelAction;
  sellerShippingAddress?: SellerShippingAddress;
  isReturnShipment?: boolean;
}

const ShippingInfo = ({
  shipmentDetails,
  action,
  sellerShippingAddress,
  isReturnShipment = false,
}: ShippingInfoProps) => {
  const { t } = useTranslation();

  const {
    chosenCarrier,
    shippingLabelUrl,
    trackingNumber,
    labelUrl = '',
    dropOffLocationLink,
    shippingOption,
  } = shipmentDetails || {};

  const { carrierName, iconUrl, carrierNote } = chosenCarrier || {};

  const shippingActionBtnRef = useRef<HTMLButtonElement>(null);

  const shippingType = useMemo(() => {
    switch (shippingOption) {
      case ShippingPickupTypeEnum.homeCollection:
        return t('MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.SHIPPING_OPTION.PICK_UP');
      case ShippingPickupTypeEnum.dropOff:
        return t('MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.SHIPPING_OPTION.DROP_OFF');
      default:
        return '';
    }
  }, [shippingOption, t]);

  const showAddressCard =
    !!action?.type && ['getShippingLabel', 'editShippingDetails'].includes(action.type) && !isReturnShipment;

  if (!shipmentDetails) {
    return showAddressCard ? (
      <AddressCard
        address={sellerShippingAddress}
        action={action}
      />
    ) : null;
  }

  return (
    <div>
      <div className={styles[`${classNamePath}`]}>
        <div className={styles[`${classNamePath}__carrier`]}>
          <img
            alt={carrierName}
            src={iconUrl}
            className={styles[`${classNamePath}__carrier--image`]}
          />
          <div className={styles[`${classNamePath}__carrier--name`]}>{carrierName}</div>
          {!!shippingType && <div className={styles[`${classNamePath}__carrier--name`]}>{`- ${shippingType}`}</div>}
        </div>

        <div className={styles[`${classNamePath}__viewDropOff`]}>
          <div className={styles[`${classNamePath}__viewDropOff--inner`]}>
            <ShippingInstructionDescription
              shipmentDetails={shipmentDetails}
              carrierWebsite={dropOffLocationLink}
            />
          </div>
        </div>

        {trackingNumber && (
          <div className={styles[`${classNamePath}__viewTracking`]}>
            <div className={styles[`${classNamePath}__viewTracking--inner`]}>
              {t('MY_ACCOUNT.ORDERS_SALES.ACTION.GET_SHIPPING_LABEL.TRACKING')}:
              <span className={styles[`${classNamePath}__viewTracking--number`]}>{trackingNumber}</span>
              <Icon
                name="duplicate-default"
                className={styles[`${classNamePath}__viewTracking--icon`]}
              />
            </div>
          </div>
        )}

        {carrierNote && <div dangerouslySetInnerHTML={{ __html: carrierNote }} />}

        {labelUrl ? (
          <>
            <div className={styles[`${classNamePath}__qrCode`]}>
              <img
                src={labelUrl}
                alt="QR Code"
                className={styles[`${classNamePath}__qrCode--image`]}
              />
            </div>
            <button
              ref={shippingActionBtnRef}
              className={styles[`${classNamePath}__downloadCode`]}
            >
              {t(`${langPrefix}DOWNLOAD_CODE`)}
            </button>
          </>
        ) : (
          shippingLabelUrl && (
            <PopupActionView
              className={clsx('vc-btn vc-btn--full vc-btn--primary', styles[`${classNamePath}__downloadCodeButton`])}
            >
              {t(`${langPrefix}VIEW_SHIPPING_LABEL`)}
            </PopupActionView>
          )
        )}
      </div>

      {showAddressCard && <AddressCard address={sellerShippingAddress} />}
    </div>
  );
};

export default ShippingInfo;
